import React from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import {
  CheckCircle,
  LocationOn,
  Work,
  Email,
  Phone,
  Event,
  Description,
} from "@mui/icons-material";
import { FormDatas } from "../../types/FormData";

interface ConfirmationProps {
  formData: FormDatas;
}

const Confirmation: React.FC<ConfirmationProps> = ({ formData }) => {
  // Function to format dates in the required format: YYYYMMDDTHHMMSSZ
  const formatDate = (date: Date): string => {
    const pad = (num: number) => num.toString().padStart(2, "0");
    return (
      date.getUTCFullYear().toString() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      "T" +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) +
      "Z"
    );
  };

  const handleAddToCalendar = () => {
    if (!formData.appointment) return;

    // Parse the appointment date and assume a 1-hour duration
    const startDate = new Date(formData.appointment);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour later
    const dates = `${formatDate(startDate)}/${formatDate(endDate)}`;

    // Build event title and details
    const title = encodeURIComponent(
      `Wissenslückentest bei Lernförderung OWL bei ${formData.location} `
    );
    let details = `Rolle: ${formData.role || "Nicht angegeben"}\n`;
    details += `Telefon: ${formData.phoneNumber || "Nicht angegeben"}\n`;
    details += `E-Mail: ${formData.email || "Nicht angegeben"}\n`;
    if (formData.documents && formData.documents.length > 0) {
      const docs = formData.documents.map((doc) => doc.name).join(", ");
      details += `Dokumente: ${docs}\n`;
    }
    const encodedDetails = encodeURIComponent(details);
    const location = encodeURIComponent(formData.location || "");

    // Construct the Google Calendar URL with query parameters
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${encodedDetails}&location=${location}`;

    // Open the URL in a new tab
    window.open(googleCalendarUrl, "_blank");
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        mt: 4,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 5,
          borderRadius: "12px",
          width: "100%",
          maxWidth: "600px",
          boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* ✅ Erfolgreiche Bestätigung mit Icon */}
        <Avatar
          sx={{
            bgcolor: "green",
            width: 70,
            height: 70,
            mb: 2,
            mx: "auto",
          }}
        >
          <CheckCircle sx={{ fontSize: 45, color: "white" }} />
        </Avatar>

        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "green", mb: 1 }}
        >
          Danke, {formData.firstName || "Nutzer"}! 🎉
        </Typography>
        <Typography variant="body1" sx={{ color: "#5f6368", mb: 3 }}>
        Formular erfolgreich eingereicht
        </Typography>

        {/* ✅ Zusammenfassung der Benutzereingaben */}
        <Paper
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: "10px",
            padding: 2,
            textAlign: "left",
            mt: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <LocationOn color="primary" />
            </Grid>
            <Grid item xs>
              <Typography>
                <strong>Standort:</strong>{" "}
                {formData.location || "Nicht angegeben"}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item>
              <Work color="primary" />
            </Grid>
            <Grid item xs>
              <Typography>
                <strong>Rolle:</strong>{" "}
                {formData.role || "Nicht angegeben"}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item>
              <Phone color="primary" />
            </Grid>
            <Grid item xs>
              <Typography>
                <strong>Telefon:</strong>{" "}
                {formData.phoneNumber || "Nicht angegeben"}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item>
              <Email color="primary" />
            </Grid>
            <Grid item xs>
              <Typography>
                <strong>E-Mail:</strong> {formData.email || "Nicht angegeben"}
              </Typography>
            </Grid>
          </Grid>

          {/* ✅ Termin anzeigen, wenn vorhanden */}
          {formData.appointment && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Event color="primary" />
                </Grid>
                <Grid item xs>
                  <Typography>
                    <strong>Termin:</strong>{" "}
                    {new Date(formData.appointment).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          {/* ✅ Dokumente anzeigen, wenn vorhanden */}
          {formData.documents && formData.documents.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Description color="primary" />
                </Grid>
                <Grid item xs>
                  <Typography variant="body1" fontWeight="bold">
                    Hochgeladene Dokumente:
                  </Typography>
                  <List>
                    {formData.documents.map((doc, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={doc.name} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>

        {/* ✅ Button zum Hinzufügen des Termins in Google Calendar */}
        {formData.appointment && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddToCalendar}
            >
              Termin zu Google Calendar hinzufügen
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Confirmation;
